import React from 'react';
import { FilterState } from '../../types/album';
import { theme } from '../../styles/theme';
import { useWindowSize } from '../../hooks/useWindowSize';
import FilterChips from './FilterChips';

interface FilterPanelProps {
  filters: FilterState;
  availableGenres: string[];
  availableArtists: string[];
  availableDecades: string[];
  availableCountries: Array<{ code: string; name: string; count: number }>;
  availableLanguages: Array<{ code: string; name: string; count: number }>;
  yearRange: [number, number];
  onFiltersChange: (filters: FilterState) => void;
  isVisible: boolean;
  onClose?: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  availableGenres,
  availableArtists,
  availableDecades,
  availableCountries,
  availableLanguages,
  yearRange,
  onFiltersChange,
  isVisible,
  onClose
}) => {
  const { isMobile, isTablet } = useWindowSize();
  
  const getPanelWidth = () => {
    if (isMobile) return '100vw';
    if (isTablet) return '250px';
    return '300px';
  };
  
  const panelStyle: React.CSSProperties = {
    width: getPanelWidth(),
    height: '100vh',
    background: theme.colors.surface,
    borderRight: `1px solid ${theme.colors.border}`,
    padding: isMobile ? theme.spacing.md : theme.spacing.lg,
    overflowY: 'auto',
    transform: isVisible ? 'translateX(0)' : 'translateX(-100%)',
    transition: `transform ${theme.transitions.normal}`,
    position: 'fixed',
    left: 0,
    top: 0,
    zIndex: isMobile ? theme.zIndex.modal : theme.zIndex.sticky,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: theme.spacing.xl,
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: theme.spacing.sm,
    fontSize: '0.875rem',
    fontWeight: '600',
    color: theme.colors.text,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: '0.875rem',
  };

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, searchText: value });
  };

  const handleGenreToggle = (genre: string) => {
    const newGenres = filters.genres.includes(genre)
      ? filters.genres.filter(g => g !== genre)
      : [...filters.genres, genre];
    onFiltersChange({ ...filters, genres: newGenres });
  };

  const handleDecadeToggle = (decade: string) => {
    const newDecades = filters.decades.includes(decade)
      ? filters.decades.filter(d => d !== decade)
      : [...filters.decades, decade];
    onFiltersChange({ ...filters, decades: newDecades });
  };

  const handleCountryToggle = (countryCode: string) => {
    const newCountries = filters.countries.includes(countryCode)
      ? filters.countries.filter(c => c !== countryCode)
      : [...filters.countries, countryCode];
    onFiltersChange({ ...filters, countries: newCountries });
  };

  const handleLanguageToggle = (languageCode: string) => {
    const newLanguages = filters.languages.includes(languageCode)
      ? filters.languages.filter(l => l !== languageCode)
      : [...filters.languages, languageCode];
    onFiltersChange({ ...filters, languages: newLanguages });
  };

  const handleYearRangeChange = (range: [number, number]) => {
    onFiltersChange({ ...filters, yearRange: range });
  };


  const mobileCloseButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: theme.spacing.md,
    right: theme.spacing.md,
    background: 'rgba(0, 0, 0, 0.8)',
    color: 'white',
    fontSize: '2rem',
    padding: theme.spacing.md,
    borderRadius: theme.borderRadius.lg,
    cursor: 'pointer',
    zIndex: 10,
    minHeight: '44px',
    minWidth: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
  };

  const mobileBackdropStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: theme.zIndex.modal - 1,
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && isVisible && (
        <div 
          style={mobileBackdropStyle} 
          onClick={onClose}
        />
      )}
      
      <div style={panelStyle} className="filter-panel scrollbar-thin">
        {/* Mobile close button */}
        {isMobile && onClose && (
          <button style={mobileCloseButtonStyle} onClick={onClose}>
            ×
          </button>
        )}
        
        <div style={sectionStyle}>
          <label style={labelStyle}>Search</label>
          <input
            type="text"
            value={filters.searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search albums, artists..."
            style={inputStyle}
          />
        </div>

      <FilterChips
        title="Genres"
        items={availableGenres}
        selectedItems={filters.genres}
        onToggle={handleGenreToggle}
      />

      <div style={sectionStyle}>
        <label style={labelStyle}>Year Range</label>
        <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
          <input
            type="number"
            value={filters.yearRange[0]}
            onChange={(e) => handleYearRangeChange([parseInt(e.target.value) || yearRange[0], filters.yearRange[1]])}
            style={{ ...inputStyle, width: '80px' }}
            min={yearRange[0]}
            max={yearRange[1]}
          />
          <span style={{ color: theme.colors.textSecondary }}>-</span>
          <input
            type="number"
            value={filters.yearRange[1]}
            onChange={(e) => handleYearRangeChange([filters.yearRange[0], parseInt(e.target.value) || yearRange[1]])}
            style={{ ...inputStyle, width: '80px' }}
            min={yearRange[0]}
            max={yearRange[1]}
          />
        </div>
      </div>

      {availableDecades.length > 0 && (
        <FilterChips
          title="Decades"
          items={availableDecades}
          selectedItems={filters.decades}
          onToggle={handleDecadeToggle}
        />
      )}

      {availableCountries.length > 0 && (
        <div style={sectionStyle}>
          <label style={labelStyle}>Artist Country ({availableCountries.length})</label>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {availableCountries.slice(0, 20).map(country => (
              <div
                key={country.code}
                onClick={() => handleCountryToggle(country.code)}
                style={{
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  margin: `${theme.spacing.xs} 0`,
                  backgroundColor: filters.countries.includes(country.code) 
                    ? theme.colors.primary 
                    : theme.colors.surfaceLight,
                  color: filters.countries.includes(country.code) 
                    ? 'white' 
                    : theme.colors.text,
                  borderRadius: theme.borderRadius.sm,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{country.name}</span>
                <span style={{ 
                  fontSize: '0.75rem', 
                  opacity: 0.8,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  padding: '2px 6px',
                  borderRadius: '10px'
                }}>
                  {country.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {availableLanguages.length > 0 && (
        <div style={sectionStyle}>
          <label style={labelStyle}>Language ({availableLanguages.length})</label>
          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
            {availableLanguages.slice(0, 15).map(language => (
              <div
                key={language.code}
                onClick={() => handleLanguageToggle(language.code)}
                style={{
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  margin: `${theme.spacing.xs} 0`,
                  backgroundColor: filters.languages.includes(language.code) 
                    ? theme.colors.primary 
                    : theme.colors.surfaceLight,
                  color: filters.languages.includes(language.code) 
                    ? 'white' 
                    : theme.colors.text,
                  borderRadius: theme.borderRadius.sm,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{language.name}</span>
                <span style={{ 
                  fontSize: '0.75rem', 
                  opacity: 0.8,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  padding: '2px 6px',
                  borderRadius: '10px'
                }}>
                  {language.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
    </>
  );
};

export default FilterPanel;